import React, { useState, useEffect, useRef } from 'react';
import type { ExerciseLibraryEntry } from '../../services/endpoints/exerciseLibraryService';

// ── Muscle group display order ────────────────────────────────────────────────
const MUSCLE_GROUP_ORDER = [
  'Chest', 'Back', 'Shoulders', 'Biceps', 'Triceps',
  'Quads', 'Hamstrings', 'Glutes', 'Calves',
  'Abs', 'Traps', 'Forearms',
];

// Equipment badge colors
const EQUIPMENT_COLORS: Record<string, string> = {
  Barbell:    'bg-orange-900/40 text-orange-300',
  Dumbbell:   'bg-blue-900/40 text-blue-300',
  Cable:      'bg-green-900/40 text-green-300',
  Machine:    'bg-purple-900/40 text-purple-300',
  Bodyweight: 'bg-gray-700 text-gray-300',
  Other:      'bg-gray-700 text-gray-400',
};
const equipmentColor = (eq: string | null) =>
  eq ? (EQUIPMENT_COLORS[eq] ?? 'bg-gray-700 text-gray-400') : '';

// ── Props ─────────────────────────────────────────────────────────────────────
interface ExercisePickerProps {
  library: ExerciseLibraryEntry[];
  onSelect: (entry: ExerciseLibraryEntry) => void;
  onClose: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────
const ExercisePicker: React.FC<ExercisePickerProps> = ({ library, onSelect, onClose }) => {
  const [activeGroup, setActiveGroup] = useState<string>('All');
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  // Focus search on open
  useEffect(() => { searchRef.current?.focus(); }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Derive the list of muscle groups present in the library, in preferred order
  const muscleGroupsInLibrary = MUSCLE_GROUP_ORDER.filter(g =>
    library.some(e => e.muscleGroup === g)
  );

  // Filter exercises
  const filtered = library.filter(e => {
    const matchesGroup = activeGroup === 'All' || e.muscleGroup === activeGroup;
    const matchesSearch = !search.trim() ||
      e.name.toLowerCase().includes(search.toLowerCase()) ||
      (e.equipment ?? '').toLowerCase().includes(search.toLowerCase());
    return matchesGroup && matchesSearch;
  });

  // Group filtered results by muscle group for section headers
  const grouped: Record<string, ExerciseLibraryEntry[]> = {};
  for (const entry of filtered) {
    if (!grouped[entry.muscleGroup]) grouped[entry.muscleGroup] = [];
    grouped[entry.muscleGroup].push(entry);
  }
  const orderedGroups = MUSCLE_GROUP_ORDER.filter(g => grouped[g]?.length > 0);

  return (
    // Backdrop
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Modal panel */}
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 flex-shrink-0">
          <div>
            <h2 className="text-white font-semibold text-lg">Browse Exercise Library</h2>
            <p className="text-gray-500 text-xs mt-0.5">{library.length} exercises · click to select</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xl leading-none px-2 py-1 rounded hover:bg-gray-700 transition-colors"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pt-4 pb-2 flex-shrink-0">
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search exercises or equipment…"
            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:border-blue-500 focus:outline-none"
          />
        </div>

        {/* Muscle group filter chips */}
        <div className="px-5 pb-3 flex flex-wrap gap-1.5 flex-shrink-0">
          {['All', ...muscleGroupsInLibrary].map(g => (
            <button
              key={g}
              onClick={() => setActiveGroup(g)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                activeGroup === g
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white border border-gray-700'
              }`}
            >
              {g}
            </button>
          ))}
        </div>

        {/* Exercise list */}
        <div className="overflow-y-auto flex-1 px-5 pb-5 space-y-5">
          {orderedGroups.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-10">No exercises match your search.</p>
          ) : (
            orderedGroups.map(group => (
              <div key={group}>
                {/* Section header — only shown when viewing "All" or searching */}
                {(activeGroup === 'All' || search.trim()) && (
                  <h3 className="text-xs font-semibold text-blue-400 uppercase tracking-widest mb-2 pt-1">
                    {group}
                  </h3>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {grouped[group].map(entry => (
                    <button
                      key={entry.exerciseLibraryId}
                      onClick={() => { onSelect(entry); onClose(); }}
                      className="flex items-center justify-between bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-blue-500 rounded-lg px-3 py-2.5 text-left transition-colors group"
                    >
                      <span className="text-white text-sm font-medium group-hover:text-blue-300 transition-colors leading-snug">
                        {entry.name}
                      </span>
                      {entry.equipment && (
                        <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ml-2 ${equipmentColor(entry.equipment)}`}>
                          {entry.equipment}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ExercisePicker;
